import { Handle, Position } from '@xyflow/react';
import {
  Stack,
  Select,
  NumberInput,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Text,
  Switch,
  SegmentedControl,
} from '@mantine/core';
import { IconKeyboard, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import { VariableTextarea } from '../../components/flow-editor/variable-textarea';
import { RephraseEditor } from '../../components/flow-editor/rephrase-editor';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps, HandleDefinition } from '../types';
import { gatherWidgetCore, type GatherConfig, type DigitMapping } from '@legacy-shared/widgets/core/gather';

export { type GatherConfig, type DigitMapping } from '@legacy-shared/widgets/core/gather';

const inputTypeOptions = [
  { value: 'dtmf', label: 'DTMF (Keypad)' },
  { value: 'speech', label: 'Speech' },
  { value: 'dtmf speech', label: 'DTMF + Speech' },
];

const digitOptions = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '*', label: '* (Star)' },
  { value: '#', label: '# (Pound)' },
];

const finishKeyOptions = [
  { value: '#', label: '# (Pound)' },
  { value: '*', label: '* (Star)' },
];

const HANDLE_SPACING = 20;
const HANDLE_TOP_OFFSET = 15;

function gatherHandleOffset(output: HandleDefinition, index: number, config: GatherConfig) {
  if (output.position === 'right') return `${HANDLE_TOP_OFFSET + index * HANDLE_SPACING}px`;
  if (output.id !== 'timeout') return '70%';
  return config.mode !== 'gather' && !config.handleInvalid ? '50%' : '30%';
}

function GatherNode({ id, data, selected }: WidgetNodeProps<GatherConfig>) {
  const { digitMappings, mode } = data.config;
  const isGather = mode === 'gather';
  const outputs = getDynamicOutputs(data.config);

  const subtitle = isGather
    ? data.config.outputVariable || 'gather'
    : `${digitMappings.length} option${digitMappings.length !== 1 ? 's' : ''}`;

  const configured = isGather ? !!data.config.prompt : digitMappings.length > 0 && !!data.config.prompt;

  const handleCount = outputs.filter((o) => o.position === 'right').length;
  const minHeight = handleCount > 2 ? HANDLE_TOP_OFFSET + handleCount * HANDLE_SPACING : undefined;

  return (
    <WidgetNodeShell
      icon={IconKeyboard}
      title={data.label || 'Gather'}
      subtitle={subtitle}
      configured={configured}
      errors={data.errors}
      selected={selected}
      color="indigo"
      minHeight={minHeight}
      retryCount={data.config.retryCount}
    >
      <Handle type="target" position={Position.Top} id="in" />

      {outputs.map((o, index) => (
        <LabeledHandle
          key={o.id}
          type="source"
          position={o.position}
          id={o.id}
          label={o.label}
          offset={gatherHandleOffset(o, index, data.config)}
          nodeId={id}
        />
      ))}
    </WidgetNodeShell>
  );
}

function GatherOptionsPanel({
  config,
  onChange,
  availableVariables,
  zeroRouteEnabled,
  languages,
  defaultLanguage,
}: ConfigPanelProps<GatherConfig>) {
  const isGather = config.mode === 'gather';
  const availableDigitOptions = zeroRouteEnabled ? digitOptions.filter((d) => d.value !== '0') : digitOptions;
  const repeatDigitOptions = [
    { value: '', label: 'Disabled' },
    ...availableDigitOptions.filter((d) => d.value !== config.finishOnKey),
  ];

  const addMapping = () => {
    const usedDigits = new Set(config.digitMappings.map((m) => m.digit));
    const availableDigit = availableDigitOptions.find((d) => !usedDigits.has(d.value))?.value || '1';

    onChange({
      ...config,
      digitMappings: [...config.digitMappings, { id: `digit-${Date.now()}`, digit: availableDigit, label: '' }],
    });
  };

  const updateMapping = (index: number, updates: Partial<DigitMapping>) => {
    onChange({
      ...config,
      digitMappings: config.digitMappings.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    });
  };

  const removeMapping = (index: number) => {
    onChange({
      ...config,
      digitMappings: config.digitMappings.filter((_, i) => i !== index),
    });
  };

  return (
    <Stack gap="md">
      <div>
        <Text size="sm" fw={500} mb={4}>
          Mode
        </Text>
        <SegmentedControl
          fullWidth
          value={config.mode || 'menu'}
          onChange={(value) => onChange({ ...config, mode: value as 'menu' | 'gather' })}
          data={[
            { value: 'menu', label: 'Menu' },
            { value: 'gather', label: 'Gather' },
          ]}
        />
        <Text size="xs" c="dimmed" mt={4}>
          {isGather
            ? 'Collect multi-digit input (e.g. account numbers)'
            : 'Single-digit branching (e.g. Press 1 for Sales)'}
        </Text>
      </div>

      <Select
        label="Input Type"
        description="How the caller provides input"
        value={config.inputType}
        onChange={(value) => onChange({ ...config, inputType: value || 'dtmf' })}
        data={inputTypeOptions}
      />

      <NumberInput
        label="Timeout (seconds)"
        description="How long to wait for input"
        value={config.timeout}
        onChange={(value) => onChange({ ...config, timeout: typeof value === 'number' ? value : 5 })}
        min={1}
        max={30}
      />

      {isGather && (
        <Select
          label="Finish Key"
          description="Key that submits input (usually #)"
          value={config.finishOnKey}
          onChange={(value) => {
            const finishOnKey = value || '#';
            onChange({
              ...config,
              finishOnKey,
              repeatDigit: config.repeatDigit === finishOnKey ? undefined : config.repeatDigit,
            });
          }}
          data={finishKeyOptions}
        />
      )}

      {isGather && (
        <NumberInput
          label="Number of Digits"
          description="Leave blank for variable-length input terminated by finish key"
          value={config.numDigits ?? ''}
          onChange={(value) => {
            const numDigits = typeof value === 'number' ? value : undefined;
            onChange({ ...config, numDigits, repeatDigit: numDigits === 1 ? undefined : config.repeatDigit });
          }}
          min={1}
          placeholder="Variable length"
          allowDecimal={false}
        />
      )}

      {isGather && (
        <Select
          label="Repeat Digit"
          description={
            config.numDigits === 1
              ? 'Not available with a 1-digit input length — a single keypress is the input itself'
              : 'Caller can press this key by itself to hear the prompt again'
          }
          value={config.repeatDigit ?? ''}
          onChange={(v) => onChange({ ...config, repeatDigit: v || undefined })}
          data={repeatDigitOptions}
          disabled={config.numDigits === 1}
        />
      )}

      {!isGather && (
        <Switch
          label="Handle Invalid Input"
          description="Create output for when caller presses unmapped key"
          checked={config.handleInvalid}
          onChange={(e) => onChange({ ...config, handleInvalid: e.target.checked })}
        />
      )}

      <TextInput
        label="Save to Variable"
        description="Name of the variable to store the collected input"
        placeholder="digits"
        value={config.outputVariable}
        onChange={(e) => onChange({ ...config, outputVariable: e.target.value })}
      />

      {!isGather && (
        <>
          <Text size="sm" fw={500} mt="md">
            Digit Mappings
          </Text>
          <Text size="xs" c="dimmed">
            Each mapping creates an output on the widget for routing.
          </Text>

          {config.digitMappings.map((mapping, index) => (
            <Group key={mapping.id} gap="xs">
              <Select
                w={80}
                value={mapping.digit}
                onChange={(value) => updateMapping(index, { digit: value || '1' })}
                data={availableDigitOptions}
              />
              <TextInput
                flex={1}
                placeholder={mapping.repeat ? 'Repeat' : 'Label (e.g., Sales)'}
                value={mapping.repeat ? 'Repeat' : mapping.label}
                onChange={(e) => updateMapping(index, { label: e.target.value })}
                disabled={mapping.repeat}
              />
              <ActionIcon
                variant={mapping.repeat ? 'filled' : 'subtle'}
                color="orange"
                onClick={() => updateMapping(index, { repeat: !mapping.repeat, label: mapping.repeat ? '' : 'Repeat' })}
                title="Repeat message"
              >
                <IconRefresh size={16} />
              </ActionIcon>
              <ActionIcon variant="subtle" color="red" onClick={() => removeMapping(index)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}

          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={addMapping}
            disabled={config.digitMappings.length >= availableDigitOptions.length}
          >
            Add Digit Mapping
          </Button>
        </>
      )}

      <Text size="sm" fw={500} mt="md">
        Retry
      </Text>
      <Text size="xs" c="dimmed">
        {isGather
          ? 'Retries trigger when the caller provides no input before the timeout.'
          : 'Retries trigger when the caller provides no input or presses an unmapped key.'}
      </Text>

      <NumberInput
        label="Retry Count"
        description="Number of times to re-prompt before routing to timeout/invalid (0 to disable)"
        value={config.retryCount ?? 0}
        onChange={(value) => onChange({ ...config, retryCount: typeof value === 'number' ? value : 0 })}
        min={0}
        max={5}
        allowDecimal={false}
      />

      {(config.retryCount ?? 0) > 0 && (
        <>
          <VariableTextarea
            label="Retry Prompt"
            description="What the caller hears on retry (uses main prompt if blank)"
            placeholder="We didn't receive your input. Please try again..."
            value={config.retryPrompt ?? ''}
            onChange={(retryPrompt) => onChange({ ...config, retryPrompt })}
            availableVariables={availableVariables}
            languages={languages}
            defaultLanguage={defaultLanguage}
            minRows={3}
          />
          <RephraseEditor
            message={config.retryPrompt ?? ''}
            rephrases={config.retryPromptRephrases}
            onChange={(retryPromptRephrases) => onChange({ ...config, retryPromptRephrases })}
            languages={languages}
            defaultLanguage={defaultLanguage}
          />
        </>
      )}
    </Stack>
  );
}

function GatherPromptPanel({
  config,
  onChange,
  availableVariables,
  languages,
  defaultLanguage,
}: ConfigPanelProps<GatherConfig>) {
  return (
    <Stack gap="xs" style={{ flex: 1 }}>
      <VariableTextarea
        label="Prompt"
        description="What the caller hears before input"
        placeholder={
          config.mode === 'gather'
            ? 'Please enter your account number followed by the pound key...'
            : 'Press 1 for sales, 2 for support...'
        }
        value={config.prompt}
        onChange={(prompt) => onChange({ ...config, prompt })}
        availableVariables={availableVariables}
        languages={languages}
        defaultLanguage={defaultLanguage}
        minRows={3}
        style={{ flex: 1 }}
        styles={{
          root: { display: 'flex', flexDirection: 'column', flex: 1 },
          wrapper: { flex: 1 },
          input: { height: '100%' },
        }}
      />
      <RephraseEditor
        message={config.prompt}
        rephrases={config.promptRephrases}
        onChange={(promptRephrases) => onChange({ ...config, promptRephrases })}
        languages={languages}
        defaultLanguage={defaultLanguage}
      />
    </Stack>
  );
}

function getDynamicOutputs(config: GatherConfig): HandleDefinition[] {
  const outputs: HandleDefinition[] = [];

  if (config.mode === 'gather') {
    outputs.push({
      id: 'out',
      type: 'default' as const,
      label: 'Collected',
      position: 'bottom' as const,
    });
  } else {
    for (const mapping of config.digitMappings) {
      if (mapping.repeat) continue;
      outputs.push({
        id: mapping.id,
        type: 'digit' as const,
        label: mapping.label || mapping.digit,
        position: 'right' as const,
      });
    }
  }

  outputs.push({
    id: 'timeout',
    type: 'timeout' as const,
    label: 'No Input',
    position: 'bottom' as const,
  });

  if (config.mode !== 'gather' && config.handleInvalid) {
    outputs.push({
      id: 'invalid',
      type: 'nomatch' as const,
      label: 'Invalid',
      position: 'bottom' as const,
    });
  }

  return outputs;
}

export const gatherWidget: WidgetDefinition<GatherConfig> = {
  ...gatherWidgetCore,
  icon: 'IconKeyboard',
  variables: gatherWidgetCore.variables,

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [],
  },

  getDynamicOutputs,

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: GatherNode,
    configPanel: GatherOptionsPanel,
    configTabs: [
      { value: 'options', label: 'Options', panel: GatherOptionsPanel },
      { value: 'prompt', label: 'Prompt', panel: GatherPromptPanel },
    ],
    preview: (config) =>
      config.mode === 'gather' ? config.outputVariable || 'gather' : `${config.digitMappings.length} options`,
  },

  validation: {
    ...gatherWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
